import React from 'react';
import styled from 'styled-components';
import { Trans } from '@lingui/macro';
import type { Card as CardModel } from '../types';
import {
  ACTION_CHIP_BG,
  ACTION_DECORATION_CLIP,
  MASKED_GLYPH_CLIP,
  PHYSICAL_GRADIENT,
  VIRTUAL_GRADIENT,
  cardArtImageSet,
  lastFour,
} from './styles';

/**
 * The card face — the shared visual rendered by both the desktop grid
 * (`CardTile`) and the mobile carousel (`CardCarousel`). Bespoke
 * styled-component faithful to `reference/card-ui.css` (PRD §5, Option A).
 *
 * White text / badge border sit on the FIXED dark card art, so they stay
 * literal (genuinely bespoke chrome, not theme-driven). `cardUrl` / `actionUrl`
 * hand off to the host verbatim via `onNavigate` → `navigate:native` (PRD §8).
 */

const Root = styled.div<{ $type: CardModel['type']; $suspended: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 311 / 201;
  border-radius: 16px;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
  background: ${({ $type, $suspended }) =>
    $suspended ? 'transparent' : $type === 'PHYSICAL' ? PHYSICAL_GRADIENT : VIRTUAL_GRADIENT};
`;

const Background = styled.div<{ $type: CardModel['type']; $suspended: boolean }>`
  position: absolute;
  inset: 0;
  background-size: cover;
  background-image: ${({ $type }) => cardArtImageSet($type)};
  opacity: ${({ $suspended }) => ($suspended ? 0.64 : 1)};
`;

const Badge = styled.div`
  position: absolute;
  top: 16px;
  right: 17px;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #e1e4e4;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 10px;
  line-height: 16px;
  letter-spacing: 0.2px;
  color: #ffffff;
  text-transform: uppercase;
`;

const Footer = styled.div`
  position: absolute;
  left: 16px;
  bottom: 17px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  max-width: calc(100% - 32px);
`;

const Title = styled.span`
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
  line-height: normal;
`;

const Number = styled.span`
  display: inline-flex;
  align-items: center;
  /* 4px between the masked glyphs — matches the legacy, where the glyphs are
     inline-blocks separated by whitespace (~a space char wide). */
  gap: 4px;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: #ffffff;
`;

const Masked = styled.span`
  width: 11px;
  height: 11px;
  display: inline-block;
  background: #ffffff;
  aspect-ratio: 1.051;
  clip-path: ${MASKED_GLYPH_CLIP};
`;

const Pan = styled.span`
  margin-left: 4px;
`;

const Overlay = styled.button`
  position: absolute;
  inset: 0;
  z-index: 1;
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;
`;

const Action = styled.button`
  position: absolute;
  top: 25%;
  left: 0;
  width: calc(100% - 32px);
  margin: 0 16px;
  padding: 8px 12px;
  z-index: 1;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  border: none;
  border-radius: 8px;
  color: #000000;
  background: ${ACTION_CHIP_BG};
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  cursor: pointer;
  font: inherit;
  text-align: left;
`;

const ActionDecoration = styled.span`
  margin: 5px;
  width: 10px;
  aspect-ratio: 1;
  flex-shrink: 0;
  background-color: #000000;
  clip-path: ${ACTION_DECORATION_CLIP};
`;

type Props = {
  card: CardModel;
  /** Mobile carousel: dim non-selected cards. */
  faded?: boolean;
  onNavigate: (route: string) => void;
};

export const Card: React.FC<Props> = ({ card, faded = false, onNavigate }) => (
  <Root
    $type={card.type}
    $suspended={card.status === 'SUSPENDED'}
    className={card.status}
    style={faded ? { opacity: 0.3, transition: 'opacity 0.3s ease-out' } : undefined}
  >
    <Background $type={card.type} $suspended={card.status === 'SUSPENDED'} />
    <Badge>
      {card.type === 'PHYSICAL' ? <Trans>PHYSICAL</Trans> : <Trans>VIRTUAL</Trans>}
    </Badge>
    <Footer>
      <Title>{card.aliasName}</Title>
      <Number>
        <Masked />
        <Masked />
        <Masked />
        <Masked />
        <Pan>{lastFour(card.truncatedPan)}</Pan>
      </Number>
    </Footer>

    {card.cardUrl && (
      <Overlay
        type="button"
        aria-label={card.aliasName}
        onClick={() => onNavigate(card.cardUrl as string)}
      />
    )}

    {card.action && card.actionUrl && (
      <Action
        type="button"
        onClick={() => onNavigate(card.actionUrl as string)}
      >
        <span>{card.action}</span>
        <ActionDecoration aria-hidden />
      </Action>
    )}
  </Root>
);
